import { Component, ViewChild, ElementRef, Injector } from '@angular/core';
import type { OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { IonicModule, IonButton } from '@ionic/angular';
import { timer } from 'rxjs';

import { AuthService, LoaderService, LocalStorageService } from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import { Constants } from '@shared/models';
import type { TLoginCredentials } from '@shared/models';

@Component({
    'selector': 'app-login',
    'templateUrl': './login.page.html',
    'styleUrls': ['./login.page.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        IonicModule
    ]
})
export class LoginPage extends ViewBasePage implements OnInit {
    @ViewChild('password', { read: ElementRef }) private passwordRef: ElementRef;
    @ViewChild('loginButton') private loginButton: IonButton;
    private passwordVisible = false;
    public user: any = {};
    public vm: any = { 'loginText': 'Login' };
    public showLoginDiv = true;
    // public saveAttempt = false;
    public submitted = false;

    constructor(private authService: AuthService, private loaderService: LoaderService,
        private localStorageService: LocalStorageService, private elementRef: ElementRef, injector: Injector) {
        super(injector);
        super.setCurrentView(Constants.VIEW_LOGIN);
    }

    public override async ngOnInit(): Promise<void> {
        this.vm.version = Constants.APPLICATION_VERSION;
        this.checkForLoginCreds();

        // // When the internet connection state changes then re/enable the login button.
        // this.connectivityService.monitor().subscribe(isConnected => {
        //     this.showInternetConnectionState(isConnected);
        // });
        this.eventsService.subscribe(Constants.EVENT_INTERNET_CONNECTION_CHANGE, async (data: any) => {
            this.onInternetConnectionStateChangeEvent(data);
        });

        // The above monitor will trigger when the state changes.  It does nothing to
        // initialize the state.  This setTimeout function will set the initial state of the button.
        // This code will not work if it's not in a setTimeout block!
        timer(30).subscribe(() => {
            const isOnline = this.connectivityService.isOnline();
            this.onInternetConnectionStateChangeEvent(isOnline);
        });

        // setTimeout(() => {
        //     const isOnline = this.connectivityService.isOnline();
        //     this.showInternetConnectionState(isOnline);
        // });

        // this.connectivityService.getMergedObservable().subscribe(isConnected => {
        // const isOnline = this.connectivityService.isOnline();
        // this.showInternetConnectionState(isOnline);
        // this.connectivityService.monitor().subscribe(isConnected => {
        //     this.showInternetConnectionState(isConnected);
        // });
    }

    protected override onInternetConnectionStateChangeEvent(isConnected: boolean): void {
        this.vm.loginText = isConnected ? 'Login' : 'No Internet Available';
        this.loginButton.disabled = !isConnected;
    }

    private async checkForLoginCreds() {
        this.user.email = await this.localStorageService.get('shuttleNomixLoginEmail');
        this.user.password = await this.localStorageService.get('shuttleNomixLoginPassword');
    }

    public isEmailValid(): boolean {
        const validEmail = this.utilsService.validateEmailAddress(this.user.email);
        return validEmail;
    }

    public onToggleLoginVisibility() {
        this.showLoginDiv = !this.showLoginDiv;
        return this.showLoginDiv;
    }

    public onEyeClick() {
        this.passwordVisible = !this.passwordVisible;
        if (this.passwordVisible) {
            this.passwordRef.nativeElement.setAttribute('type', 'text');
        } else {
            this.passwordRef.nativeElement.setAttribute('type', 'password');
        }
    }

    public async onLoginClick(form: NgForm) {
        this.submitted = true;
        if (!form.valid) {
            return;
        }

        try {
            // const validEmail  = this.utilsService.validateEmailAddress(this.user.email);
            if (!this.utilsService.validateEmailAddress(this.user.email)) {
                form.controls['email'].setErrors({ 'invalidError': true });
                return;
            }
            if (!this.utilsService.validatePassword(this.user.password)) {
                form.controls['password'].setErrors({ 'invalidError': true });
                return;
            }

            const email = this.user.email;
            const password = this.user.password;

            // implement this later:  return IonicDeployService.periodicallyCheckForNewVersion();

            await this.uxHelperService.showSpinner('Logging you in.');
            const cred: TLoginCredentials = await this.authService.login(email, password);
            await this.loaderService.prepareSession(cred.userUid);

            // ✅ CRITICAL FIX: Use replaceUrl to remove LoginPage from stack
            const result = await this.router.navigateByUrl('/dashboard', { 
                replaceUrl: true  // ← This removes the login from DOM
            });


            await this.uxHelperService.hideSpinner();
            this.eventsService.publish(Constants.EVENT_LOGIN);
        } catch (err) {
            this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    public async onResetPasswordClick() {
        if (!this.user.email) {
            await this.uxHelperService.showAlert('Error', 'Your email address is required.');
            return;
        }

        try {
            await this.uxHelperService.showSpinner('Sending email.');
            const success = await this.authService.sendPasswordResetEmail(this.user.email);
            await this.uxHelperService.hideSpinner();
            if (success) {
                await this.uxHelperService.showAlert('Email Successfully Sent',
                    'Please follow the instructions in the email to reset your password.');
            } else {
                await this.uxHelperService.showAlert('Email Error',
                    'Please verify the spelling of the email address and your internet connection.');
            }
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            throw err;
        }
    }
}
